import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { LocalStorageService } from '../storage/local-storage.service';
import { environment } from '../../../environments/environment';
import {
  NetworkException,
  ApiException,
  UnauthorizedException,
  NotFoundException,
  ServerException,
  TimeoutException
} from './errors';

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  // 1. Bypass local assets (like translations)
  if (req.url.startsWith('/assets/') || req.url.startsWith('./assets/')) {
    return next(req);
  }

  const localStorage = inject(LocalStorageService);
  const token = localStorage.getString('auth_token'); 

  // Prefix relative URLs with the environment API Base URL
  const isAbsoluteUrl = req.url.startsWith('http');
  const url = isAbsoluteUrl ? req.url : `${environment.apiBaseUrl}${req.url}`;

  let authReq = req.clone({ url });
  
  // 2. 👉 THE FIX: Do not attach Authorization headers to public endpoints
  const isPublicAuthRoute = req.url.includes('/auth/login') || req.url.includes('/auth/register');
  const isAuthRoute = req.url.includes('/auth/');

  if (token && !isPublicAuthRoute) {
    authReq = authReq.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  if (!environment.production) {
    console.log(`[HTTP] Sending ${authReq.method} ${authReq.url}`, authReq.body || '');
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!environment.production) {
        console.error(`[HTTP] Error ${authReq.method} ${authReq.url}`, error);
      }

      let customError: Error;

      if (error.error instanceof ErrorEvent) {
        customError = new NetworkException('Connection error or no internet.');
      } else {
        const statusCode = error.status;
        const message = error.error?.message || error.error?.error || error.message || 'Request failed.';

        if (statusCode === 401) {
          if (!isAuthRoute && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('lbc:session-expired'));
          }
          customError = new UnauthorizedException(message);
        } else if (statusCode === 404) {
          customError = new NotFoundException(message);
        } else if (statusCode === 408 || statusCode === 504) {
          customError = new TimeoutException(message);
        } else if (statusCode >= 500) {
          customError = new ServerException(message, statusCode);
        } else {
          customError = new ApiException(message, statusCode, error);
        }
      }

      return throwError(() => customError);
    })
  );
};
